import {defaultPropertyDefinitions, partPropertyCategories} from "@buildwithflux/constants";
import {
    AnyPcbNode,
    createSubjectExpressionContext,
    DEFAULT_SYMBOL_NAME,
    ElementHelper,
    getDefaultValueProperty,
    getElectricalExpressionDependencies,
    getIsExcludedFromBOM,
    getManufacturerPropertyValueAsString,
    getProperty,
    getPropertyValue,
    getPropertyValueAsString,
    IExpressionEvaluationSchematicSubjectContext,
    isExpression,
    pcbLayoutNestedNodeDelimiter,
    stringifyElectricalString,
} from "@buildwithflux/core";
import type {ElectricalContextDocumentDataSubsetSubset} from "@buildwithflux/core";
import type {
    IElementData,
    IElementsMap,
    IExtractedElementData,
    IPartVersionData,
    IPropertyData,
    IPropertiesMap,
    PartSourcingProperties,
} from "@buildwithflux/models";
import {getPartType, matchesPartType} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";
import {get, set} from "lodash";
import createCachedSelector from "re-reselect";
import {createSelector} from "reselect";

import {DocumentStorageHelper} from "../../../../storage_engine/helpers/DocumentStorageHelper";
import {PartStorageHelper} from "../../../../storage_engine/helpers/PartStorageHelper";
import {computeDocumentConfigSectionWithOwnerDefault, computeElectricalDocumentSubset} from "../document/derived";
import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Derived selectors for elements - computed values
 * These selectors perform filtering, mapping, or other computations on element data
 * Note: These use reselect for memoization of expensive computations
 */

/** Gets element designators (labels) - memoized */
export const designators: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const element of Records.iterate(elements)) {
            if (element.label) {
                result.push(element.label);
            }
        }
        return result;
    },
);

/** Gets non-branch point elements - memoized */
export const nonBranchPointElements: DocumentSelector<IElementData[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): IElementData[] => {
        if (!elements) return [];
        const result: IElementData[] = [];
        for (const element of Records.iterate(elements)) {
            if (!ElementHelper.isBranchPointElement(element)) {
                result.push(element);
            }
        }
        return result;
    },
);

/** Gets non-branch point element UIDs - memoized */
export const nonBranchPointElementUids: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const [uid, element] of Object.entries(elements)) {
            if (element?.part_version_data_cache && !ElementHelper.isBranchPointElement(element)) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets elements by part type - parameterized and memoized */
export const byPartType: ParameterizedSelector<string, IElementData[]> = (partType: string) =>
    createSelector([(state: DocumentStoreState) => state.elements], (elements): IElementData[] => {
        if (!elements) return [];
        const result: IElementData[] = [];
        for (const element of Records.iterate(elements)) {
            if (element.part_version_data_cache && getPartType(element.part_version_data_cache) === partType) {
                result.push(element);
            }
        }
        return result;
    });

/** Gets elements filtered by a predicate - parameterized */
export const filtered: ParameterizedSelector<(element: IElementData) => boolean, IElementData[]> = (
    predicate: (element: IElementData) => boolean,
) => {
    return (state: DocumentStoreState): IElementData[] => {
        const elements = state.elements;
        if (!elements) return [];
        const result: IElementData[] = [];
        for (const element of Records.iterate(elements)) {
            if (predicate(element)) {
                result.push(element);
            }
        }
        return result;
    };
};

/** Checks if an element exists by UID */
export const exists: ParameterizedSelector<string, boolean> = (uid: string) => {
    return (state: DocumentStoreState): boolean => {
        return state.elements?.[uid] !== undefined;
    };
};

/** Gets element UIDs that match a predicate - parameterized */
export const uidsByPredicate: ParameterizedSelector<(element: IElementData) => boolean, string[]> = (
    predicate: (element: IElementData) => boolean,
) => {
    return (state: DocumentStoreState): string[] => {
        const elements = state.elements;
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const [uid, element] of Object.entries(elements)) {
            if (element && predicate(element)) {
                result.push(uid);
            }
        }
        return result;
    };
};

/** Gets locked element UIDs */
export const lockedUids: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const [uid, element] of Object.entries(elements)) {
            if (element?.locked) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets unique part UIDs used in the document - memoized */
export const partUidsInDocument: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        if (!elements) return emptyArray;
        const partUids = new Set<string>();
        for (const element of Object.values(elements)) {
            if (element.part_version) {
                partUids.add(element.part_version_data_cache.part_uid);
            }
        }
        return Array.from(partUids);
    },
);

/** Gets symbol designator suffixes from terminal elements - memoized */
export const symbolDesignatorSuffixes: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        return computeSymbolDesignatorSuffixes(elements);
    },
);

/** Checks if an element (or any parent module) has "Exclude from BOM" = true - parameterized */
export const isExcludedFromBomWithModules: ParameterizedSelector<string, boolean> = (elementUid: string) => {
    return (state: DocumentStoreState): boolean => {
        if (!elementUid || !state.elements) {
            return false;
        }

        const [baseUid, ...nestedUids] = elementUid.split(pcbLayoutNestedNodeDelimiter);
        if (!baseUid) {
            return false;
        }

        let element: IElementData | IExtractedElementData | undefined = state.elements?.[baseUid];

        if (element?.properties && getIsExcludedFromBOM(element.properties)) {
            return true;
        }

        for (const nestedUid of nestedUids) {
            const subMap = element?.part_version_data_cache?.extracted_element_cache ?? {};
            element = subMap[nestedUid];
            if (!element) {
                break;
            }
            if (element.properties && getIsExcludedFromBOM(element.properties)) {
                return true;
            }
        }

        return false;
    };
};

/** Gets unique property values across all elements for a given property name - parameterized */
export const propertyValues: ParameterizedSelector<string, string[]> = (propertyName: string) =>
    createSelector([(state: DocumentStoreState) => state.elements], (elements): string[] => {
        return computeElementPropertyValues(elements, propertyName);
    });

/** Gets part sourcing properties for an element - parameterized */
export const partSourcingProperties: ParameterizedSelector<string, PartSourcingProperties | null> = (
    elementUid: string,
) => {
    return (state: DocumentStoreState): PartSourcingProperties | null => {
        const element = state.elements?.[elementUid];
        if (!element) return null;

        const name = element.part_version_data_cache.name;
        const properties = element.properties;
        const manufacturerName = getManufacturerPropertyValueAsString(properties) || null;
        const manufacturerPartNumber =
            getPropertyValueAsString(defaultPropertyDefinitions.mpn.label, properties)?.trim() || null;
        return {
            manufacturerName,
            manufacturerPartNumber,
            partName: name ?? null,
            partUid: element.part_uid ?? null,
        };
    };
};

/** Gets the visibility toggle map for an element - parameterized by elementUid and currentUserUid */
export const visibilityToggleMap = (
    elementUid: string,
    currentUserUid: string | undefined,
): ((state: DocumentStoreState) => {[key: string]: boolean}) => {
    return (state: DocumentStoreState): {[key: string]: boolean} => {
        const element = state.elements?.[elementUid];
        const section = `metrics-${elementUid}`;
        const userVisibilityToggleMap = computeDocumentConfigSectionWithOwnerDefault(
            section,
            currentUserUid,
            state.owner_uid,
            state.configs,
        );
        return computeElementVisibilityToggleMap(element, userVisibilityToggleMap);
    };
};

/** Gets visible properties for an element sorted by order - parameterized */
export const visibleProperties = (
    elementUid: string,
    currentUserUid: string | undefined,
): ((state: DocumentStoreState) => IPropertyData[]) => {
    return (state: DocumentStoreState): IPropertyData[] => {
        const element = state.elements?.[elementUid];
        const properties = element?.properties ?? {};
        const section = `metrics-${elementUid}`;
        const userVisibilityToggleMap = computeDocumentConfigSectionWithOwnerDefault(
            section,
            currentUserUid,
            state.owner_uid,
            state.configs,
        );
        const visibilityMap = computeElementVisibilityToggleMap(element, userVisibilityToggleMap);
        return Object.values(properties)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .filter((property) => visibilityMap[property.uid]);
    };
};

/** Gets count of visible properties for an element - parameterized */
export const numVisibleProperties = (
    elementUid: string,
    currentUserUid: string | undefined,
): ((state: DocumentStoreState) => number) => {
    return (state: DocumentStoreState): number => {
        const element = state.elements?.[elementUid];
        const properties = element?.properties ?? {};
        const section = `metrics-${elementUid}`;
        const userVisibilityToggleMap = computeDocumentConfigSectionWithOwnerDefault(
            section,
            currentUserUid,
            state.owner_uid,
            state.configs,
        );
        const visibilityMap = computeElementVisibilityToggleMap(element, userVisibilityToggleMap);
        return computeNumVisibleProperties(properties, visibilityMap);
    };
};

/** Gets merged visibility toggle map across multiple selected elements - parameterized */
export const mergedVisibilityToggleMap = (
    selectedObjectUids: string[],
    selectedElementsCount: number,
    parentUid: string,
    currentUserUid: string | undefined,
): ((state: DocumentStoreState) => {[key: string]: boolean | number}) => {
    return (state: DocumentStoreState): {[key: string]: boolean | number} => {
        const ownerUid = state.owner_uid;
        const documentConfigs = state.configs;
        const elements = state.elements;

        if (selectedElementsCount === 1) {
            const element = elements?.[parentUid];
            const section = `metrics-${parentUid}`;
            const userMap = computeDocumentConfigSectionWithOwnerDefault(
                section,
                currentUserUid,
                ownerUid,
                documentConfigs,
            );
            return computeElementVisibilityToggleMap(element, userMap);
        }

        const elementVisibilityMaps: Array<{
            visibilityMap: {[key: string]: boolean};
            properties: IPropertiesMap | undefined;
        }> = [];

        for (const uid of selectedObjectUids) {
            if (!uid) continue;
            const element = elements?.[uid];
            if (!element) continue;

            const section = `metrics-${uid}`;
            const userMap = computeDocumentConfigSectionWithOwnerDefault(
                section,
                currentUserUid,
                ownerUid,
                documentConfigs,
            );
            elementVisibilityMaps.push({
                visibilityMap: computeElementVisibilityToggleMap(element, userMap),
                properties: element.properties,
            });
        }

        return computeMergedVisibilityToggleMap(elementVisibilityMaps);
    };
};

/** Gets the count of element designators (keys in elements map) */
export const designatorsCount: DocumentSelector<number> = (state: DocumentStoreState): number => {
    const elements = state.elements;
    if (!elements) return 0;
    return Object.keys(elements).length;
};

/** Gets an element's 3D model asset storage name - parameterized */
export const element3DModelAssetStorageName: ParameterizedSelector<string, string | undefined> = (
    elementUid: string,
) => {
    return (state: DocumentStoreState): string | undefined => {
        const elementData = state.elements?.[elementUid];
        if (!elementData?.part_version_data_cache) return undefined;
        return PartStorageHelper.get3dModelFromAsset(elementData);
    };
};

/** Gets an element's footprint asset storage name - parameterized */
export const elementFootprintAssetStorageName: ParameterizedSelector<string, string | undefined> = (
    elementUid: string,
) => {
    return (state: DocumentStoreState): string | undefined => {
        const elementData = state.elements?.[elementUid];
        if (!elementData?.part_version_data_cache) return undefined;
        return PartStorageHelper.getFootprintAsset(elementData);
    };
};

/** Gets an element's metamodule data - parameterized */
export const elementMetaModule = (
    elementUid: string,
): ((state: DocumentStoreState) => IElementData["metamodule"] | null) => {
    return (state: DocumentStoreState): IElementData["metamodule"] | null => {
        return state.elements?.[elementUid]?.metamodule ?? null;
    };
};

/** Gets an attribute from an element's part_version_data_cache - parameterized */
export const elementPartVersionDataCacheAttribute = <T>(
    elementUid: string,
    attributeName: keyof IPartVersionData,
): ((state: DocumentStoreState) => T | undefined) => {
    return (state: DocumentStoreState): T | undefined => {
        return state.elements?.[elementUid]?.part_version_data_cache?.[attributeName] as T | undefined;
    };
};

/** Gets a specific attribute from an element - parameterized */
export const elementAttribute = <T>(
    attributeName: keyof IElementData,
    elementUid: string | undefined,
    fallbackValue?: T,
): ((state: DocumentStoreState) => T | undefined) => {
    return (state: DocumentStoreState): T | undefined => {
        if (!elementUid) return undefined;
        const element = state.elements?.[elementUid];
        if (!element) return fallbackValue;
        const value = element[attributeName];
        return (value ?? fallbackValue) as T | undefined;
    };
};

/** Gets part data for update - parameterized by partUidsForUpdate and selectAll */
export const partDataForUpdate = (
    partUidsForUpdate: string[],
    selectAll: boolean,
): ((state: DocumentStoreState) => IPartVersionData[]) => {
    return (state: DocumentStoreState): IPartVersionData[] => {
        const elements = state.elements;
        if (!elements) return [];
        const uniquePartVersions = DocumentStorageHelper.getUniquePartVersions(Object.values(elements));
        return computeFilteredPartVersions(uniquePartVersions, partUidsForUpdate, selectAll);
    };
};

// ============================================================================
// Pure compute functions (not selectors - take parameters directly)
// ============================================================================

/**
 * Pure function to count visible properties.
 * Counts directly without creating intermediate arrays.
 */
export function computeNumVisibleProperties(
    properties: IPropertiesMap | undefined,
    visibilityMap: {[key: string]: boolean},
): number {
    if (!properties) return 0;
    let count = 0;
    for (const property of Object.values(properties)) {
        if (visibilityMap[property.uid]) {
            count++;
        }
    }
    return count;
}

/**
 * Pure function to filter part versions by a list of part UIDs.
 * Uses Set for O(1) lookups instead of indexOf which is O(n).
 */
export function computeFilteredPartVersions(
    uniquePartVersions: IPartVersionData[],
    partUidsForUpdate: string[],
    selectAll: boolean,
): IPartVersionData[] {
    if (selectAll) return uniquePartVersions;
    const partUidSet = new Set(partUidsForUpdate);
    return uniquePartVersions.filter((partVersion) => partUidSet.has(partVersion.part_uid));
}

/**
 * Pure function to compute the element visibility toggle map.
 * This determines which properties and metrics should be visible for an element.
 *
 * @param element - The element data
 * @param userVisibilityToggleMap - The user's visibility settings from document configs
 * @returns A map of property/metric UIDs to their visibility state
 */
export function computeElementVisibilityToggleMap(
    element: IElementData | undefined,
    userVisibilityToggleMap: {[key: string]: unknown},
): {[key: string]: boolean} {
    const elementProperties = element?.properties ?? {};
    const defaultProperty = getDefaultValueProperty(elementProperties);

    // Get visibility settings from metamodule that are not in properties
    const notInProperties: Array<[string, boolean]> = [];
    const metamoduleVisibilityMap = element?.metamodule?.visibilityToggleMap ?? {};
    for (const [key, value] of Object.entries(metamoduleVisibilityMap)) {
        if (!(key in elementProperties)) {
            notInProperties.push([key, userVisibilityToggleMap[key] === true || value === true]);
        }
    }

    // Build the visibility map from properties
    const propertyEntries: Array<[string, boolean]> = [];
    for (const [key, property] of Object.entries(elementProperties)) {
        const enabledByDefault = property.uid === defaultProperty?.uid;
        const hasVisibilityInfo =
            userVisibilityToggleMap[key] !== undefined || metamoduleVisibilityMap[key] !== undefined;
        const enabled = userVisibilityToggleMap[key] === true || metamoduleVisibilityMap[key] === true;
        // A special type of property is the default one, which is visible by default
        // For this reason we want it to be explicitly set to false to be hidden, defaulting
        // to true if its visibility is undefined
        if (enabled || (enabledByDefault && !hasVisibilityInfo)) {
            propertyEntries.push([key, Boolean(property)]);
        }
    }

    // Cast userVisibilityToggleMap entries to boolean since they come from document configs
    const userVisibilityEntries: Array<[string, boolean]> = [];
    for (const [key, value] of Object.entries(userVisibilityToggleMap)) {
        userVisibilityEntries.push([key, Boolean(value)]);
    }

    return Object.fromEntries([...propertyEntries, ...notInProperties, ...userVisibilityEntries]);
}

/**
 * Pure function to compute unique property values across all elements.
 * This is used for dynamic autocomplete suggestions.
 *
 * @param elements - The elements map
 * @param propertyName - The property name to search for
 * @returns Array of unique property values
 */
export function computeElementPropertyValues(elements: IElementsMap | undefined, propertyName: string): string[] {
    if (!elements) {
        return [];
    }

    const values = new Set<string>();
    for (const element of Object.values(elements)) {
        const properties = element.properties;
        if (properties) {
            for (const property of Object.values(properties)) {
                if (property.name === propertyName && property.value && typeof property.value === "string") {
                    values.add(property.value);
                }
            }
        }
    }

    return Array.from(values);
}

/**
 * Pure function to merge visibility toggle maps from multiple elements.
 * For multi-selection, merges visibility maps across all elements,
 * using property names as keys and marking properties with different visibility
 * states as "mixed" (value 2).
 *
 * @param elementVisibilityMaps - Array of objects containing element visibility map and properties
 * @returns A map of property names to their visibility state (boolean or 2 for mixed)
 */
export function computeMergedVisibilityToggleMap(
    elementVisibilityMaps: Array<{
        visibilityMap: {[key: string]: boolean};
        properties: IPropertiesMap | undefined;
    }>,
): {[key: string]: boolean | number} {
    const mergedVisibilityMap: {[key: string]: boolean | number} = {};

    for (let currentIndex = 0; currentIndex < elementVisibilityMaps.length; currentIndex++) {
        const item = elementVisibilityMaps[currentIndex];
        if (!item) continue;

        const {visibilityMap, properties} = item;

        // For batch edit, properties don't have a unique uid, so we use the name as the key
        for (const [propertyUid, visibility] of Object.entries(visibilityMap)) {
            const propertyName = properties?.[propertyUid]?.name ?? propertyUid;

            // If it's the first element, just add the visibility
            if (currentIndex === 0) {
                mergedVisibilityMap[propertyName] = visibility;
            } else {
                // If the property already exists and has a different visibility, mark as mixed
                if (
                    propertyName in mergedVisibilityMap &&
                    mergedVisibilityMap[propertyName] !== visibility &&
                    mergedVisibilityMap[propertyName] !== 2
                ) {
                    mergedVisibilityMap[propertyName] = 2;
                } else if (!(propertyName in mergedVisibilityMap)) {
                    mergedVisibilityMap[propertyName] = visibility;
                }
            }
        }
    }

    return mergedVisibilityMap;
}

/**
 * Pure function to compute the evaluated value of an element's property.
 * This evaluates expressions in property values using the element's context.
 * @param element The element data
 * @param linkedPcbLayoutElementNode The linked PCB layout node
 * @param property The property to evaluate
 * @param document The electrical document subset for expression evaluation
 * @returns The evaluated property value as a string, or undefined if not evaluable
 */
export function computeElementPropertyEvaluatedValue(
    element: IElementData | IExtractedElementData | undefined,
    linkedPcbLayoutElementNode: AnyPcbNode | undefined,
    property: IPropertyData | undefined,
    document: ElectricalContextDocumentDataSubsetSubset | undefined,
): string | undefined {
    if (!property || !document) {
        return undefined;
    }

    if (typeof property.value === "string" && isExpression(property.value)) {
        if (element && linkedPcbLayoutElementNode) {
            const deps = getElectricalExpressionDependencies(property.value);

            const context = createSubjectExpressionContext(element, document, linkedPcbLayoutElementNode);

            const atomicContext: Partial<IExpressionEvaluationSchematicSubjectContext> = {};

            if (deps) {
                for (const dep of deps) {
                    set(atomicContext, dep, get(context, dep));
                }
            }

            return stringifyElectricalString(property.value, property.unit, atomicContext);
        }
    }
    return undefined;
}

/**
 * Memoized selector for element property evaluated value.
 * Uses createCachedSelector to provide per-(elementUid, propertyKey) memoization,
 * matching the behavior of the original Redux selector.
 * This prevents expensive expression evaluation from running on every store update.
 */
export const selectElementPropertyEvaluatedValue = createCachedSelector(
    (state: DocumentStoreState) => state.elements,
    (state: DocumentStoreState) => state.pcbLayoutNodes,
    (state: DocumentStoreState) =>
        computeElectricalDocumentSubset(
            state.uid,
            state.description,
            state.name,
            state.owner_handle,
            state.slug,
            state.properties,
        ),
    (_state: DocumentStoreState, elementUid: string) => elementUid,
    (_state: DocumentStoreState, _elementUid: string, propertyKey: string) => propertyKey,
    (elements, pcbLayoutNodes, document, elementUid, propertyKey) => {
        const element = elements?.[elementUid];
        const property = element?.properties?.[propertyKey] ?? getProperty(propertyKey, element?.properties ?? {});
        const linkedNode = pcbLayoutNodes?.[elementUid];

        return computeElementPropertyEvaluatedValue(element, linkedNode, property, document);
    },
)((_state, elementUid, propertyKey) => `${elementUid}:${propertyKey}`);

export function computeSymbolDesignatorSuffixes(elements: IElementsMap | undefined): string[] {
    if (!elements) return [DEFAULT_SYMBOL_NAME];

    const symbolNames: (string | undefined)[] = [];
    for (const element of Object.values(elements)) {
        if (
            matchesPartType(getPartType(element.part_version_data_cache), partPropertyCategories.terminal, {
                includeParents: false,
            })
        ) {
            const value = getPropertyValue(
                defaultPropertyDefinitions.subsymbol_designator_suffix.label,
                element.properties,
            );
            symbolNames.push(value?.toString());
        }
    }

    // Filter out undefined, null, and empty strings, then get unique values
    const uniqueNames = new Set(symbolNames.map((n) => n || DEFAULT_SYMBOL_NAME));
    return Array.from(uniqueNames);
}
